// WHAT A STORY IS ALLOWED TO SHOW YOU (busy-timeline-srv#27, AI Brain spec §5).
//
// One rule, stated once, in both directions:
//
//   • a PARENT sees its own things and meaningful activity from its descendants, with the
//     relationship visible — a borrowed step says which child it happened in ("via Prada");
//   • a CHILD sees ONLY itself and its descendants. Not its parent, and not its siblings.
//
// The sidebar used to do the second half backwards: standing inside a child it anchored on the
// family ROOT and listed the parent with every sibling, so opening a sub-story showed you mostly
// other people's stories. The one link upwards a child keeps is the back-link above its title
// (lib/subOngoing.js `parentBackLink`) — a way out, not a view of the family.
//
// Pure: records in, rows out. No store, no router.

/**
 * The sidebar's rows for the ongoing that is open: itself, then its OWN children (oldest first,
 * the order their steps appear in the middle timeline).
 *
 * @param items  every ongoing the store knows
 * @param currentId the ongoing being viewed, or null when none is
 * @returns `[{ o, child }]`, empty when the current ongoing is not in the list yet
 */
export function scopedFamilyRows(items, currentId) {
  const list = items || []
  const id = currentId ? String(currentId) : ''
  if (!id) return []
  const current = list.find(o => String(o?.id) === id)
  if (!current) return []
  const kids = list
    .filter(o => o && String(o.parentOngoingId || '') === id)
    .slice()
    .sort((a, b) => new Date(a.createdDate || a.updateDate) - new Date(b.createdDate || b.updateDate))
  return [{ o: current, child: false }, ...kids.map(o => ({ o, child: true }))]
}

/**
 * The relationship tag for a story step, or null for a step that happened here.
 *
 * The server tags a borrowed step with the descendant it came from (`viaOngoingId` / `viaTitle`);
 * the tag is a LINK, so it carries the id as well as the words — seeing "via Prada" and not being
 * able to open Prada would be the relationship stated and then withheld.
 */
export function viaTag(step) {
  const id = step?.viaOngoingId
  if (!id) return null
  const title = String(step?.viaTitle || '').trim()
  return { id: String(id), title: title || 'sub-ongoing', text: `via ${title || 'sub-ongoing'}` }
}

/**
 * The step kinds that point at an entity WITHOUT being its step (lib/storyLifecycle's KINDS, spelled
 * here so this module does not import that one — storySteps sits between them).
 */
export const ABOUT_ENTITY_KINDS = new Set(['overdue', 'question', 'purchase', 'receipt', 'reminder'])

/**
 * One object, one row — however many scoped views it arrives through (§5: "one object appears
 * through several scoped views without duplication").
 *
 * A step's identity is the ENTITY it names (`entityRef` when the server typed it, else `entityId`),
 * because that is the thing on screen: the same task announced on a child's story and folded onto
 * its parent is one task. A step naming no entity is only itself and keeps its own id, which cannot
 * collide. The FIRST occurrence wins, so a caller that puts its own steps first keeps them.
 */
export function dedupeSteps(steps) {
  const seen = new Set()
  const out = []
  for (const s of steps || []) {
    if (!s) continue
    // A step ABOUT an entity is not that entity's step (busy-my-web#409): "Task overdue", Busy's
    // question, the purchase and the receipt all point at the task they concern, and the entity id is
    // how the story remembers what it already asked — but each is a thing that happened in its own
    // right, not a second announcement of the task. Keyed by itself, so the task's step does not
    // swallow it.
    const about = ABOUT_ENTITY_KINDS.has(s.kind)
    const ref = about ? null : (s.entityRef?.id || s.entityId)
    const key = ref ? `${s.entityRef?.type || '?'}:${ref}` : `step:${s.id ?? out.length}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(s)
  }
  return out
}

/**
 * The lifecycle steps the story WRITES but no timeline DRAWS (Pouria, 2026-10-01: "we dont need two
 * last item we have in story and ongoing timeline").
 *
 * "Task overdue" and "Next step" (lib/storyLifecycle's KINDS.overdue / KINDS.question) each repeated
 * something already on screen: the task's own row says "Overdue — was due …", and the question is
 * asked — with its verbs — in the Follow-ups strip under the story. They are still WRITTEN: the
 * `question` event is the marker storyLifecycle reads to know a question was asked, is open, or was
 * answered (`raisesFor`, `lifecycleRows`, `questionFor`), so only the drawing stops. The purchase,
 * receipt, reminder and complete steps are things that happened and stay.
 *
 * One rule for both panes: lib/storySteps' `buildStorySteps` — the list the middle AND the Ongoing
 * timeline rail are built from — drops these before it builds a step, so neither can draw one.
 */
export const UNDRAWN_STEP_KINDS = new Set(['overdue', 'question'])

/** True for a story timeline event (or a step built from one) that no timeline draws. */
export function isUndrawnStep(step) {
  if (!step) return false
  return UNDRAWN_STEP_KINDS.has(step.kind) || UNDRAWN_STEP_KINDS.has(step.stepKind)
}
